import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { FormattedMessage } from 'react-intl';
import { Typography, Stack, CircularProgress, List } from 'components/ui';
import { ContentContainer } from 'components/styles_components/container';
import SpacesQuery from 'queries/space';
import { useSpace, getStoredSpaceId } from 'contexts/space';
import { DashboardLink } from 'utils/link';
import Logo from 'components/layout/logo';
import { IndexListItemButton } from 'components/styles_components/list';

const Spaces = () => {
  const navigate = useNavigate();
  const { selectSpace } = useSpace();

  const { data, loading, error } = useQuery(SpacesQuery, {
    fetchPolicy: 'network-only',
  });

  const spaces = useMemo(() => data?.spaces?.spaces || [], [data]);

  useEffect(() => {
    if (loading || error || !data) return;

    if (spaces.length === 0) {
      return;
    }

    if (spaces.length === 1) {
      selectSpace(spaces[0].id);
      navigate(DashboardLink(), { replace: true });
      return;
    }

    const storedId = getStoredSpaceId();
    if (storedId && spaces.some((s) => s.id === storedId)) {
      selectSpace(storedId);
      navigate(DashboardLink(), { replace: true });
    }
  }, [data, loading, error, spaces, selectSpace, navigate]);

  const handleSelect = (space) => {
    selectSpace(space.id);
    navigate(DashboardLink(), { replace: true });
  };
  if (loading) {
    return (
      <ContentContainer maxWidth="xl">
        <CircularProgress />
      </ContentContainer>
    );
  }

  if (spaces.length === 0) {
    return (
      <ContentContainer maxWidth="xl">
        <Stack spacing={3} alignItems="center">
          <Logo width={50} />
          <Typography variant="h5">
            <FormattedMessage id="spaces.no_spaces" />
          </Typography>
          <Typography color="text.secondary">
            <FormattedMessage id="spaces.no_spaces.description" />
          </Typography>
        </Stack>
      </ContentContainer>
    );
  }

  if (spaces.length === 1) {
    return (
      <ContentContainer maxWidth="xl">
        <CircularProgress />
      </ContentContainer>
    );
  }

  return (
    <ContentContainer maxWidth="xl">
      <Stack spacing={4} alignItems="center" sx={{ width: '100%'}}>
        <Logo width={50} />
        <Typography variant="h5" fontWeight={600}>
          <FormattedMessage id="spaces.select" />
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          <FormattedMessage id="spaces.select.description" />
        </Typography>
        <List>
          {spaces.map((space) => (
            <IndexListItemButton key={space.id} onClick={() => handleSelect(space)}>
              <Typography variant="h6">{space.name}</Typography>
            </IndexListItemButton>
          ))}
        </List>
      </Stack>
    </ContentContainer>
  );
};

export default Spaces;
